import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { Checkbox } from '@neo/primitives/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Slider } from '@neo/primitives/slider'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { HelpCircle, Info, Settings } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { EnhancedTextArea } from 'v2/shared/components/enhanced-textarea'
import { z } from 'zod'
import { NodevarList } from '../../components/nodevar-list'
import { BACKGROUND_AUDIO_OPTIONS, BackgroundAudioType } from '../../config/background-audio.config'
import type { v2validations } from '../../config/validation.config'
import type { RProviders } from '../types'
import { getSttFields } from './stream-stt'
import { getTtsFields, TtsAdvancedFields } from './stream-tts'
import type { FormValues } from './stream-utils'

// Base fields that are always visible
export const getBaseFields = (
  validations: ReturnType<typeof v2validations>['stream'],
): GenericFieldConfig<FormValues> => [
  /* {
    name: 'mode',
    label: 'Stream type',
    componentType: 'radioGroup',
    zodValidation: validations.shape.mode,
    inputProps: {
      options: [
        { label: 'Audio', value: 'audio' },
        { label: 'Text', value: 'text' },
      ],
    },
    className: 'col-span-full',
  }, */
  {
    name: 'websocket_endpoint',
    label: 'WSS Endpoint',
    componentType: 'input',
    zodValidation: validations.shape.websocket_endpoint,
    inputProps: {
      placeholder: 'wss://example.com/ws',
    },
    className: 'col-span-full',
  },
]

// Noise cancellation level slider (shown when noise cancellation is enabled)
const NoiseCancellationLevelSlider = ({ form }: { form: GenericFormType<FormValues> }) => {
  const level = form.useStore((state) => state.values.noise_cancellation_level ?? 85)

  const handleLevelChange = (value: number[]) => {
    form.setFieldValue('noise_cancellation_level', value[0])
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          type="button"
          data-testid="button-noise-cancellation-settings"
        >
          <Settings className="w-5 h-5 text-secondary" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto">
        <div className="flex flex-col gap-2 w-64">
          <Typography variant="P/500" className="text-primary">
            Noise cancellation level
          </Typography>
          <div className="flex items-center gap-3">
            <Slider
              value={[level]}
              onValueChange={handleLevelChange}
              min={60}
              max={100}
              step={1}
              data-testid="slider-noise-cancellation-level"
            />
            <span className="text-p-500 font-jetbrains tabular-nums text-primary w-10 text-right">
              {level}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-s-400 text-secondary">60</span>
            <span className="text-s-400 text-secondary">100</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Audio mode checkboxes component
export const AudioModeCheckboxes = ({ form }: { form: GenericFormType<FormValues> }) => {
  const mode = form.useStore((state) => state.values.mode)
  const enableNoiseCancellation = form.useStore(
    (state) => state.values.enable_noise_cancellation ?? false,
  )

  if (mode !== 'audio') {
    return null
  }

  return (
    <div className="flex items-center justify-between col-span-full">
      <Checkbox
        name="enable_noise_cancellation"
        label="Enable noise cancellation"
        value={enableNoiseCancellation}
        onChange={(checked) => form.setFieldValue('enable_noise_cancellation', checked)}
      />
      <div className={enableNoiseCancellation ? '' : 'invisible'}>
        <NoiseCancellationLevelSlider form={form} />
      </div>
    </div>
  )
}

// Greeting message field with variable support
export const GreetingMessageField = ({
  form,
  nodeVars,
}: {
  form: GenericFormType<FormValues>
  nodeVars?: string[]
}) => {
  const firstMessage = form.useStore((state) => state.values.first_message ?? '')

  const handleChange = useCallback(
    (value: string) => {
      form.setFieldValue('first_message', value)
    },
    [form],
  )

  return (
    <EnhancedTextArea
      name="first_message"
      label="Greeting message"
      value={firstMessage}
      onChange={handleChange}
      placeholder="Enter greeting message text. Use {{ to insert variables."
      showFileUpload={false}
      className="min-h-[80px]"
      endIcon={<NodevarList nodevars={nodeVars} inputElementSelector="#first_message .ql-ref" />}
    />
  )
}

// Background audio volume slider for stream text mode
const StreamBgVolumeSlider = ({ form }: { form: GenericFormType<FormValues> }) => {
  const volume = form.useStore((state) => state.values.background_audio?.volume ?? 1)

  const handleVolumeChange = (value: number[]) => {
    form.setFieldValue('background_audio.volume', value[0])
  }

  return (
    <div className="flex flex-col gap-2 w-64">
      <Typography variant="P/500" className="text-primary">
        Background audio volume
      </Typography>
      <div className="flex items-center gap-3">
        <Slider
          value={[volume]}
          onValueChange={handleVolumeChange}
          min={0}
          max={2}
          step={0.01}
          data-testid="slider-stream-background-audio-volume"
        />
        <span className="text-p-500 font-jetbrains tabular-nums text-primary w-10 text-right">
          {volume.toFixed(2)}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-s-400 text-secondary">Min</span>
        <span className="text-s-400 text-secondary">Max</span>
      </div>
    </div>
  )
}

// Background audio selector for stream text mode
const StreamBackgroundAudioConfig = ({ form }: { form: GenericFormType<FormValues> }) => {
  const audioType = form.useStore((state) => state.values.background_audio?.type)
  const isAudioEnabled = audioType && audioType !== BackgroundAudioType.NONE

  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <FieldsRenderer
            fields={[
              {
                name: 'background_audio.type',
                componentType: 'select',
                label: (
                  <div className="flex items-center gap-1 text-s-500 text-secondary mb-1">
                    Background audio
                    <Tooltip content="Play subtle ambient audio during the call">
                      <Info className="size-3 text-tertiary" />
                    </Tooltip>
                  </div>
                ),
                zodValidation: z.string().default('none'),
                inputProps: {
                  placeholder: 'Select background audio',
                  options: BACKGROUND_AUDIO_OPTIONS.map((opt) => ({
                    label: opt.label,
                    value: opt.value,
                  })),
                },
              },
            ]}
            form={form}
          />
        </div>
        {isAudioEnabled && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                data-testid="button-stream-background-audio-settings"
                className="mt-5"
              >
                <Settings className="w-5 h-5 text-secondary" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto">
              <StreamBgVolumeSlider form={form} />
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  )
}

// Text mode specific component with tabs
export const TextModeConfig = ({
  form,
  providers,
  languages,
  nodeVars,
}: {
  form: GenericFormType<FormValues>
  providers?: RProviders
  languages?: { code: string; name: string; country: string }[]
  nodeVars?: string[]
}) => {
  const [activeTab, setActiveTab] = useState('stt')

  const mode = form.useStore((state) => state.values.mode)
  const playGreeting = form.useStore((state) => state.values.play_greeting ?? false)

  const handleGreetingToggle = useCallback(
    (checked: boolean) => {
      form.setFieldValue('play_greeting', checked)
      if (!checked) {
        form.setFieldValue('first_message', '')
      }
    },
    [form],
  )

  const sttFields = useMemo(() => getSttFields(providers, languages), [providers, languages])
  const ttsFields = useMemo(() => getTtsFields(providers, languages), [providers, languages])

  // Only render for text mode
  if (mode !== 'text') {
    return null
  }

  return (
    <div className="flex flex-col gap-4 col-span-full">
      <Checkbox
        name="play_greeting"
        label={
          <span className="flex items-center gap-1">
            Play greeting message on connect
            <Tooltip content="The greeting message will be spoken when the call connects">
              <HelpCircle className="h-4 w-4 text-tertiary" />
            </Tooltip>
          </span>
        }
        value={playGreeting}
        onChange={handleGreetingToggle}
      />

      {/* Greeting message textarea with variable support - only shown when play_greeting is checked */}
      {playGreeting && <GreetingMessageField form={form} nodeVars={nodeVars} />}

      {/* STT/TTS Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList variant="default" className="grid grid-cols-2">
          <TabsTrigger value="stt" variant="default">
            Speech-to-text settings
          </TabsTrigger>
          <TabsTrigger value="tts" variant="default">
            Text-to-speech settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stt" className="mt-4">
          <div className="flex flex-col gap-4">
            <FieldsRenderer form={form} fields={sttFields} />
          </div>
        </TabsContent>

        <TabsContent value="tts" className="mt-4">
          <div className="flex flex-col gap-4">
            <FieldsRenderer form={form} fields={ttsFields} />
            <TtsAdvancedFields form={form} />
            <StreamBackgroundAudioConfig form={form} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
